import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

/**
 * Formats a date to 'DD-MMM-YY' format (e.g., '25-lug-25')
 * @param {Date|string} date - The date to format
 * @returns {string} - Formatted date or 'N/A' if date is missing
 */
const formatDate = (date) => {
  if (!date) return 'N/A';
  return dayjs(date).locale('it').format('DD-MMM-YY').toLowerCase();
};

/**
 * Formats weekdays array to Italian day names
 * @param {Array} weekdays - Array of weekday numbers (0=Sunday, 1=Monday, etc.)
 * @returns {string} - Formatted weekdays or 'N/A'
 */
const formatWeekdays = (weekdays) => {
  if (!weekdays || !Array.isArray(weekdays) || weekdays.length === 0) return 'N/A';
  
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  return weekdays.map(day => dayNames[day]).join(', ');
};

/**
 * Formats minLevel to readable format
 * @param {number} minLevel - The minimum level
 * @returns {string} - Formatted level
 */
const formatMinLevel = (minLevel) => {
  if (!minLevel && minLevel !== 0) {
    return 'Libero';
  }
  if (minLevel >= 6) {
    return 'Oltre il 5° anno';
  }
  return `${minLevel}° anno`;
};

/**
 * Formats status to Italian
 * @param {string} status - The status
 * @returns {string} - Italian status
 */
const formatStatus = (status) => {
  switch (status) {
    case 'published':
      return 'Pubblicato';
    case 'draft':
      return 'Bozza';
    case 'deleted':
      return 'Eliminato';
    default:
      return status || 'N/A';
  }
};

/**
 * Formats teacher name
 * @param {Object} teacher - The teacher object
 * @returns {string} - Teacher name or 'Da assegnare'
 */
const formatTeacher = (teacher) => {
  if (!teacher) {
    return 'Da assegnare';
  }
  const name = teacher.name || '';
  const surname = teacher.surname || '';
  return `${surname} ${name}`.trim() || 'Da assegnare';
};

/**
 * Escapes CSV values that contain commas, quotes, or newlines
 * @param {any} value - The value to escape
 * @returns {string} - Escaped CSV value
 */
const escapeCSV = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/**
 * Exports courses data to CSV file
 * @param {Array} courses - Array of course objects
 */
export const exportCoursesToCSV = (courses) => {
  if (!courses || courses.length === 0) {
    return;
  }

  // CSV Headers
  const headers = [
    'ID',
    'Nome Corso',
    'Descrizione',
    'Sede',
    'Posti Disponibili',
    'Posti Occupati',
    'Posti Rimanenti',
    'Data Inizio',
    'Data Fine',
    'Orario',
    'Stato',
    'Docente',
    'ID Docente',
    'Giorni Settimana',
    'Anno Minimo',
    'Tessera Richiesta',
    'Prezzo Mensile (€)',
    'Prezzo Annuale 1 Pagamento (€)',
    'Prezzo Annuale 3 Pagamenti (€)',
    'Data Creazione',
    'Data Aggiornamento'
  ];

  // Convert courses to CSV rows
  const csvRows = courses.map(course => {
    return [
      course._id || course.id || '',
      course.name || '',
      course.description || '',
      course.venue || '',
      course.availableSeats || 0,
      course.occupiedSeats || 0,
      course.remainingSeats || 0,
      formatDate(course.startDate),
      formatDate(course.endDate),
      course.time || '',
      formatStatus(course.status),
      formatTeacher(course.teacher),
      course.teacherId || '',
      formatWeekdays(course.weekdays),
      formatMinLevel(course.minLevel),
      course.isMembershipRequired ? 'Si' : 'No',
      course.pricing?.monthly || 0,
      course.pricing?.annual1 || 0,
      course.pricing?.annual3 || 0,
      formatDate(course.createdAt),
      formatDate(course.updatedAt)
    ];
  });

  // Create CSV content
  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...csvRows.map(row => row.map(escapeCSV).join(','))
  ].join('\n');

  // Create blob and download file
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `corsi_${dayjs().format('YYYY-MM-DD')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

